import React from 'react';

import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import Text from '~/components/core/TextComponents/Text';

import ActionStateChip from '../../ActionStateChip';
import { ACTION_NEEDS_ATTENTION, ACTION_STATE_APPEARANCE, QUESTION_STATE_APPEARANCE } from '../../constants';
import { CONVERSATION_TEXTS } from '../../conversation/texts';
import type { AskState, ConductorQuestion } from '../../types';
import { activityCardElementId, activityRowKey, formatEntryTime } from '../../utils';
import JumpLink from '../JumpLink';
import KindCardFrame from '../KindCardFrame';
import type { MessageRef } from '../types';

import AnswerForm from './AnswerForm';
import AnswerRecord from './AnswerRecord';

interface QuestionEntryProps {
  notification: StpNotification;
  /** The question this entry is about, where the asks have been read. */
  ask?: ConductorQuestion;
  state?: AskState;
  /** Refetches the asks after an answer, which both panes' ask cards resolve from. */
  onAskUpdate: () => Promise<void>;
  /** Outlines the card: the activity panel last took the reader here. */
  isSelected?: boolean;
  /** Shows a card in the activity panel, by its row's key. */
  onSelectActivityCard?: (key: string) => void;
  /**
   * The handler's message the question answers, on a question Clive raised in reply: null where the log does
   * not hold the message's entry. Absent on every other question.
   */
  inResponseTo?: MessageRef | null;
  /** Takes the reader to the handler's message, by its entry's event id. */
  onJumpToMessage: (eventId: string) => void;
  /** Drawn inside Clive's reply, which has the rail's node and the time. */
  isFolded?: boolean;
}

interface ReplyCaptionProps {
  inResponseTo: MessageRef | null;
  onJumpToMessage: (eventId: string) => void;
}

/** Which of the handler's messages a question raised in reply answers, with the way back to it where the log holds it. */
const ReplyCaption: React.FC<ReplyCaptionProps> = ({ inResponseTo, onJumpToMessage }) => {
  const label = CONVERSATION_TEXTS.IN_RESPONSE_TO(inResponseTo ? formatEntryTime(inResponseTo.sentAt) : null);

  return (
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="wrap-anywhere">
      {CONVERSATION_TEXTS.QUESTION}
      {CONVERSATION_TEXTS.SEPARATOR}
      {inResponseTo ? <JumpLink onClick={() => onJumpToMessage(inResponseTo.eventId)}>{label}</JumpLink> : label}
    </Text>
  );
};

/**
 * An entry about a question, drawn from the ask rather than the entry, which is a snapshot from when
 * it was published. The card is the question and the log is the only place it is answered: the form
 * while it waits on the handler, the record once it is closed. It carries no state chip — the pink
 * frame says it is waiting, and the record says how it closed. The activity panel lists every ask, so
 * the title can show the question's card there. A question Clive raised in reply to the handler says
 * which message it answers, over its title.
 *
 * Before the asks are read, nothing has established where the question is, so the card says what the
 * entry said, claims no state and offers no Answer, the way a request's does.
 */
const QuestionEntry: React.FC<QuestionEntryProps> = ({
  notification,
  ask,
  state,
  onAskUpdate,
  isSelected,
  onSelectActivityCard,
  inResponseTo,
  onJumpToMessage,
  isFolded,
}) => {
  const caption =
    inResponseTo === undefined ? null : <ReplyCaption inResponseTo={inResponseTo} onJumpToMessage={onJumpToMessage} />;

  return ask && state ? (
    <KindCardFrame
      notification={notification}
      appearance={QUESTION_STATE_APPEARANCE[state]}
      kind="question"
      title={ask.ask}
      subtitle={ask.summary}
      chip={caption}
      isSelected={isSelected}
      onSelect={onSelectActivityCard ? () => onSelectActivityCard(activityRowKey('question', ask.id)) : undefined}
      controls={activityCardElementId(activityRowKey('question', ask.id))}
      isFolded={isFolded}
    >
      {ACTION_NEEDS_ATTENTION[state] ? (
        <AnswerForm question={ask} onAskUpdate={onAskUpdate} />
      ) : (
        <AnswerRecord question={ask} state={state} />
      )}
    </KindCardFrame>
  ) : (
    <KindCardFrame
      notification={notification}
      appearance={ACTION_STATE_APPEARANCE.unresolved}
      kind="question"
      title={notification.title}
      subtitle={notification.metadata?.summary}
      chip={
        caption ? (
          <div className="flex min-w-0 flex-wrap items-center gap-x-12 gap-y-4">
            <ActionStateChip state="unresolved" />
            {caption}
          </div>
        ) : (
          <ActionStateChip state="unresolved" />
        )
      }
      isSelected={isSelected}
      isFolded={isFolded}
    />
  );
};

export default QuestionEntry;
